import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import WarningIcon from '@mui/icons-material/Warning';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import PeopleIcon from '@mui/icons-material/People';
import MapIcon from '@mui/icons-material/Map';
import { monitoramentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import { getTileUrl, getTileAttribution } from '../../utils/map-utils';

import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

const CONTAGEM_CENTER: [number, number] = [-19.93, -44.05];
const CORES_LINHAS = ['#1E3A5F', '#2D7D9A', '#27AE60', '#E74C3C', '#F39C12', '#8E44AD', '#E67E22', '#16A085'];
const CORES_PIE = ['#1E3A5F', '#2D7D9A', '#27AE60', '#F39C12', '#E74C3C', '#8E44AD', '#E67E22', '#16A085'];
const ALERTA_COLORS: Record<string, string> = {
  critico: '#E74C3C',
  alerta: '#F39C12',
  alto: '#F39C12',
};

const toISO = (d: Date): string => d.toISOString().slice(0, 10);
const fmtBr = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

interface VacinacaoFiltros {
  codigo_unidade: string;
  imunobiologico: string;
  sigla_dose: string;
  turno: string;
  sexo: string;
  estrategia: string;
}

const filtrosVazios: VacinacaoFiltros = {
  codigo_unidade: '',
  imunobiologico: '',
  sigla_dose: '',
  turno: '',
  sexo: '',
  estrategia: '',
};

function pivotByField<T extends Record<string, unknown>>(
  flat: T[],
  rowKey: string,
  pivotKey: string,
  valueKey: string,
): Record<string, unknown>[] {
  const map = new Map<string, Record<string, unknown>>();
  for (const item of flat) {
    const rk = String(item[rowKey] ?? '');
    if (!map.has(rk)) map.set(rk, { [rowKey]: rk });
    const row = map.get(rk)!;
    row[String(item[pivotKey])] = Number(item[valueKey] ?? 0);
  }
  return Array.from(map.values());
}

function makeVacinaIcon(color: string) {
  return new L.DivIcon({
    className: '',
    html: `<div style="width:22px;height:22px;background:${color};border:2px solid #fff;border-radius:50%;box-shadow:0 2px 5px rgba(0,0,0,0.3);"></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -11],
  });
}

const FitBounds: React.FC<{ salas: { latitude: number; longitude: number }[] }> = ({ salas }) => {
  const map = useMap();
  useEffect(() => {
    if (salas.length > 0) {
      const bounds = L.latLngBounds(salas.map((s) => [Number(s.latitude), Number(s.longitude)]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }, [map, salas]);
  return null;
};

const MonitoramentoVacinas: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtrosOpen, setFiltrosOpen] = useState(true);
  const [vacinaSelecionada, setVacinaSelecionada] = useState('');

  const hoje = new Date();
  const tresMesesAtras = new Date();
  tresMesesAtras.setMonth(tresMesesAtras.getMonth() - 3);
  const [filtroInicio, setFiltroInicio] = useState(toISO(tresMesesAtras));
  const [filtroFim, setFiltroFim] = useState(toISO(hoje));
  const [dataInicio, setDataInicio] = useState(toISO(tresMesesAtras));
  const [dataFim, setDataFim] = useState(toISO(hoje));
  const [filtros, setFiltros] = useState<VacinacaoFiltros>(filtrosVazios);
  const [filtrosAplicados, setFiltrosAplicados] = useState<VacinacaoFiltros>(filtrosVazios);

  const isOfflineMap = process.env.REACT_APP_OFFLINE_MODE === 'true';

  const handleDateChange = useCallback((di: string, df: string) => {
    setFiltroInicio(di);
    setFiltroFim(df);
  }, []);

  const handleBuscar = useCallback(() => {
    setDataInicio(filtroInicio);
    setDataFim(filtroFim);
    setFiltrosAplicados({ ...filtros });
  }, [filtroInicio, filtroFim, filtros]);

  const filtrosParams = useMemo(() => {
    const p: Record<string, string | undefined> = {};
    if (filtrosAplicados.codigo_unidade) p.codigo_unidade = filtrosAplicados.codigo_unidade;
    if (filtrosAplicados.imunobiologico) p.imunobiologico = filtrosAplicados.imunobiologico;
    if (filtrosAplicados.sigla_dose) p.sigla_dose = filtrosAplicados.sigla_dose;
    if (filtrosAplicados.turno) p.turno = filtrosAplicados.turno;
    if (filtrosAplicados.sexo) p.sexo = filtrosAplicados.sexo;
    if (filtrosAplicados.estrategia) p.estrategia = filtrosAplicados.estrategia;
    return p;
  }, [filtrosAplicados]);

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await monitoramentoService.getVacinacao(
          municipioAtivo.id,
          dataInicio,
          dataFim,
          filtrosParams,
        );
        setData(result);
        const coberturas = result?.cobertura || [];
        if (coberturas.length > 0 && !vacinaSelecionada) {
          setVacinaSelecionada(coberturas[0].imunobiologico);
        }
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Erro ao carregar dados de vacinas.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipioAtivo?.id, dataInicio, dataFim, filtrosParams]);

  const kpis = data?.kpis || {};
  const consolidado: any[] = data?.consolidado || [];
  const cobertura: any[] = data?.cobertura || [];
  const evolucaoRaw: any[] = data?.evolucao || [];
  const porUnidadeRaw: any[] = data?.porUnidade || [];
  const salasMapa: any[] = data?.salasMapa || [];
  const segmentacao = data?.segmentacao || {};
  const desempenho: any[] = data?.desempenho || [];
  const projecao: any[] = data?.projecao || [];
  const alertas: any[] = data?.alertas || [];
  const opcoes = data?.filtrosOpcoes || {};

  const coberturaChart = useMemo(
    () => cobertura.map((c: any) => ({
      imunobiologico: c.imunobiologico,
      cobertura_pct: Number(c.cobertura_pct ?? 0),
      total_doses: Number(c.total_doses ?? 0),
    })),
    [cobertura],
  );

  const evolucaoPivotada = useMemo(
    () => pivotByField(evolucaoRaw, 'mes', 'imunobiologico', 'total'),
    [evolucaoRaw],
  );

  const imunobiosEvolucao = useMemo(() => {
    const set = new Set<string>();
    evolucaoRaw.forEach((item: any) => {
      if (item.imunobiologico) set.add(String(item.imunobiologico));
    });
    return Array.from(set);
  }, [evolucaoRaw]);

  const porUnidadeTabela = useMemo(() => {
    const map = new Map<string, Record<string, any>>();
    for (const item of porUnidadeRaw) {
      const unidade = String(item.nome_unidade ?? '');
      if (!map.has(unidade)) map.set(unidade, { nome_unidade: unidade, _total: 0 });
      const row = map.get(unidade)!;
      const val = Number(item.total ?? 0);
      row[String(item.imunobiologico)] = val;
      row._total = (row._total || 0) + val;
    }
    return Array.from(map.values()).sort((a, b) => b._total - a._total);
  }, [porUnidadeRaw]);

  const imunobiosPorUnidade = useMemo(() => {
    const set = new Set<string>();
    porUnidadeRaw.forEach((item: any) => {
      if (item.imunobiologico) set.add(String(item.imunobiologico));
    });
    return Array.from(set);
  }, [porUnidadeRaw]);

  const totalGeralUnidades = useMemo(
    () => porUnidadeTabela.reduce((acc, r) => acc + (r._total || 0), 0),
    [porUnidadeTabela],
  );

  const salasComCoord = useMemo(
    () => salasMapa.filter((s: any) => s.latitude != null && s.longitude != null),
    [salasMapa],
  );

  const maxDosesSala = useMemo(
    () => Math.max(...salasComCoord.map((s: any) => Number(s.total_doses || 0)), 1),
    [salasComCoord],
  );

  const unidadesOpcoes: { codigo: number; nome: string }[] = Array.isArray(opcoes.unidades)
    ? opcoes.unidades
    : [];

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Typography variant="h5" color="text.secondary">Nenhum municipio selecionado</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Skeleton variant="text" width="30%" height={48} />
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2, mt: 3 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const renderSegmentBar = (items: any[], labelKey: string, titulo: string) => (
    <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>{titulo}</Typography>
      {items?.length > 0 ? (
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={items} layout="vertical" margin={{ left: 10, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" tickFormatter={(v) => Number(v).toLocaleString('pt-BR')} />
            <YAxis type="category" dataKey={labelKey} width={90} tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v: any) => [Number(v).toLocaleString('pt-BR'), 'Doses']} />
            <Bar dataKey="total" fill="#2D7D9A" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Sem dados</Typography>
      )}
    </Paper>
  );

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Vacinas e Imunizacao</Typography>
          <Typography variant="body1" color="text.secondary">{municipioAtivo.nome}</Typography>
          <Typography variant="caption" color="text.secondary">
            Periodo: {fmtBr(dataInicio)} a {fmtBr(dataFim)}
          </Typography>
          {isOfflineMap && (
            <Chip icon={<MapIcon />} label="Mapa local (modo offline)" size="small" color="info" sx={{ mt: 1, mr: 1 }} />
          )}
          {data?.fonte && (
            <Chip label={`Fonte: ${data.fonte.sistema}`} size="small" variant="outlined" sx={{ mt: 1 }} />
          )}
        </Box>
        <DateRangeSelector dataInicio={filtroInicio} dataFim={filtroFim} onChange={handleDateChange} />
      </Box>

      {/* Filtros RF-10.1 */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight={600}>Filtros</Typography>
          <IconButton size="small" onClick={() => setFiltrosOpen(!filtrosOpen)}>
            {filtrosOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>
        <Collapse in={filtrosOpen}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Unidade</InputLabel>
                <Select
                  value={filtros.codigo_unidade}
                  label="Unidade"
                  onChange={(e) => setFiltros({ ...filtros, codigo_unidade: e.target.value })}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {unidadesOpcoes.map((u) => (
                    <MenuItem key={u.codigo} value={String(u.codigo)}>{u.nome}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Imunobiologico</InputLabel>
                <Select
                  value={filtros.imunobiologico}
                  label="Imunobiologico"
                  onChange={(e) => setFiltros({ ...filtros, imunobiologico: e.target.value })}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {(opcoes.imunobiologicos || []).map((v: string) => (
                    <MenuItem key={v} value={v}>{v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Dose</InputLabel>
                <Select
                  value={filtros.sigla_dose}
                  label="Dose"
                  onChange={(e) => setFiltros({ ...filtros, sigla_dose: e.target.value })}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {(opcoes.doses || []).map((d: string) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Turno</InputLabel>
                <Select value={filtros.turno} label="Turno" onChange={(e) => setFiltros({ ...filtros, turno: e.target.value })}>
                  <MenuItem value="">Todos</MenuItem>
                  {(opcoes.turnos || []).map((t: string) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Estrategia</InputLabel>
                <Select value={filtros.estrategia} label="Estrategia" onChange={(e) => setFiltros({ ...filtros, estrategia: e.target.value })}>
                  <MenuItem value="">Todas</MenuItem>
                  {(opcoes.estrategias || []).map((e: string) => (
                    <MenuItem key={e} value={e}>{e}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button variant="contained" startIcon={<SearchIcon />} onClick={handleBuscar} fullWidth sx={{ height: 40 }}>
                Buscar
              </Button>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      {/* Alertas RF-10.6 */}
      {alertas.length > 0 && (
        <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2, borderLeft: '4px solid #E74C3C' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WarningIcon color="error" />
            <Typography variant="subtitle1" fontWeight={600}>Gestao critica de insumos</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {alertas.slice(0, 8).map((a: any, i: number) => (
              <Chip
                key={i}
                label={a.titulo || a.resumo}
                size="small"
                sx={{
                  bgcolor: ALERTA_COLORS[a.severidade] || '#95A5A6',
                  color: '#fff',
                }}
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* KPIs RF-10.2 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Cidadaos Atendidos"
            valor={Number(kpis.total_cidadaos ?? 0).toLocaleString('pt-BR')}
            icone={<PeopleIcon />}
            cor="#8E44AD"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Doses Aplicadas"
            valor={Number(kpis.total_doses ?? 0).toLocaleString('pt-BR')}
            icone={<VaccinesIcon />}
            cor="#1E3A5F"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Salas Ativas"
            valor={Number(kpis.salas_ativas ?? 0).toLocaleString('pt-BR')}
            icone={<MedicalServicesIcon />}
            cor="#27AE60"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Imunobiologicos"
            valor={Number(kpis.imunobiologicos_distintos ?? 0).toLocaleString('pt-BR')}
            icone={<LocalPharmacyIcon />}
            cor="#2D7D9A"
          />
        </Grid>
      </Grid>

      {/* Consolidado */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Indicadores consolidados por imunobiologico
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Imunobiologico</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Cidadaos</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Doses</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {consolidado.map((row: any, i: number) => (
                <TableRow key={i}>
                  <TableCell>{row.imunobiologico}</TableCell>
                  <TableCell align="right">{Number(row.total_cidadaos ?? 0).toLocaleString('pt-BR')}</TableCell>
                  <TableCell align="right">{Number(row.total_doses ?? 0).toLocaleString('pt-BR')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {/* Mapa RF-10.3 offline */}
      <Paper elevation={1} sx={{ mb: 3, overflow: 'hidden', borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ p: 2, pb: 0 }}>
          Salas de Vacinacao de Rotina
        </Typography>
        {salasComCoord.length > 0 ? (
          <MapContainer center={CONTAGEM_CENTER} zoom={13} scrollWheelZoom style={{ height: 420, width: '100%' }}>
            <TileLayer attribution={getTileAttribution()} url={getTileUrl()} />
            <FitBounds salas={salasComCoord} />
            {salasComCoord.map((s: any, idx: number) => {
              const doses = Number(s.total_doses || 0);
              const intensity = doses / maxDosesSala;
              const color = intensity > 0.6 ? '#27AE60' : intensity > 0.3 ? '#F39C12' : '#2D7D9A';
              return (
                <Marker
                  key={`${s.codigo_unidade}-${idx}`}
                  position={[Number(s.latitude), Number(s.longitude)]}
                  icon={makeVacinaIcon(color)}
                >
                  <Popup>
                    <div style={{ minWidth: 220, fontFamily: 'sans-serif', fontSize: 13 }}>
                      <strong>{s.nome_sala}</strong>
                      <br />Doses no periodo: <strong>{doses.toLocaleString('pt-BR')}</strong>
                      <br />{s.endereco || 'Contagem/MG'}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
            Sem salas com coordenadas para o periodo.
          </Typography>
        )}
      </Paper>

      {/* Cobertura */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Cobertura Vacinal por Imunobiologico</Typography>
        {coberturaChart.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={coberturaChart} margin={{ bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="imunobiologico" tick={{ fontSize: 10, angle: -40, textAnchor: 'end' }} height={70} />
              <YAxis tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v: any) => [`${Number(v).toFixed(1)}%`, 'Cobertura']} />
              <ReferenceLine y={95} stroke="#E74C3C" strokeDasharray="5 5" label="Meta 95%" />
              <Bar dataKey="cobertura_pct" fill="#2D7D9A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Sem dados de cobertura</Typography>
        )}
      </Paper>

      {/* Evolucao */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>Evolucao Mensal de Doses</Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Vacina</InputLabel>
            <Select value={vacinaSelecionada} label="Vacina" onChange={(e) => setVacinaSelecionada(e.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              {cobertura.map((c: any) => (
                <MenuItem key={c.imunobiologico} value={c.imunobiologico}>{c.imunobiologico}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {evolucaoPivotada.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={evolucaoPivotada}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis tickFormatter={(v) => Number(v).toLocaleString('pt-BR')} />
              <Tooltip />
              <Legend />
              {(vacinaSelecionada ? imunobiosEvolucao.filter((i) => i === vacinaSelecionada) : imunobiosEvolucao)
                .map((imuno, idx) => (
                  <Line key={imuno} type="monotone" dataKey={imuno} stroke={CORES_LINHAS[idx % CORES_LINHAS.length]} strokeWidth={2} dot={{ r: 3 }} />
                ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Sem dados de evolucao</Typography>
        )}
      </Paper>

      {/* Segmentacao RF-10.4 */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Segmentacao analitica</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>{renderSegmentBar(segmentacao.porTurno, 'turno', 'Por turno')}</Grid>
        <Grid item xs={12} md={6}>{renderSegmentBar(segmentacao.porSexo, 'sexo', 'Por sexo')}</Grid>
        <Grid item xs={12} md={6}>{renderSegmentBar(segmentacao.porFaixaEtaria, 'faixa_etaria', 'Por faixa etaria')}</Grid>
        <Grid item xs={12} md={6}>{renderSegmentBar(segmentacao.porGrupo, 'grupo_populacional', 'Por grupo populacional')}</Grid>
        <Grid item xs={12} md={6}>{renderSegmentBar(segmentacao.porLocal, 'local_atendimento', 'Por local de atendimento')}</Grid>
        <Grid item xs={12} md={6}>{renderSegmentBar(segmentacao.porEstrategia, 'estrategia', 'Por estrategia')}</Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Por tipo de dose</Typography>
            {(segmentacao.porDose || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={segmentacao.porDose} dataKey="total" nameKey="sigla_dose" cx="50%" cy="50%" outerRadius={70} label>
                    {(segmentacao.porDose || []).map((_: any, i: number) => (
                      <Cell key={i} fill={CORES_PIE[i % CORES_PIE.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Sem dados</Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>{renderSegmentBar(segmentacao.porUnidade, 'nome_unidade', 'Por unidade responsavel')}</Grid>
      </Grid>

      {/* Desempenho RF-10.5 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Doses por profissional</Typography>
            <Box sx={{ overflowX: 'auto', maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Profissional</TableCell>
                    <TableCell>Unidade</TableCell>
                    <TableCell align="right">Doses</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {desempenho.map((r: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{r.nome_profissional}</TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{r.nome_unidade}</TableCell>
                      <TableCell align="right">{Number(r.total_doses).toLocaleString('pt-BR')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Projecao de consumo</Typography>
            {projecao.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={projecao}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
                  <YAxis tickFormatter={(v) => Number(v).toLocaleString('pt-BR')} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="historico" name="Historico" stroke="#1E3A5F" strokeWidth={2} />
                  <Line type="monotone" dataKey="projetado" name="Projetado" stroke="#E74C3C" strokeDasharray="5 5" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Sem dados de projecao</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Tabela por unidade */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Doses por Unidade</Typography>
        {porUnidadeTabela.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Unidade</TableCell>
                  {imunobiosPorUnidade.map((imuno) => (
                    <TableCell key={imuno} align="right" sx={{ fontWeight: 600, fontSize: 11 }}>{imuno}</TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {porUnidadeTabela.map((row: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{row.nome_unidade}</TableCell>
                    {imunobiosPorUnidade.map((imuno) => (
                      <TableCell key={imuno} align="right">{(row[imuno] ?? 0).toLocaleString('pt-BR')}</TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{(row._total ?? 0).toLocaleString('pt-BR')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Sem dados por unidade</Typography>
        )}
      </Paper>

      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="vacinas"
          contexto="dashboard"
        />
      )}

      <ChatIAButton contexto="dashboard" dadosContexto={JSON.stringify(data)} fabSx={{ bottom: 24, right: 24, top: 'auto' }} />
    </Box>
  );
};

export default MonitoramentoVacinas;
